/**
 * PAYDAY — where the money came from, and who took it home.
 *
 * The whole point of this panel is that it shows the CHAIN, not the answer.
 * Sagliano's purse is built out of two rolls and six multiplications, and a
 * screen that printed "Winnings: $6.91M" would throw away everything
 * interesting about it. So it reads the way the broadcast graphic would:
 *
 *     68,400 through the gate · 90% of a 76,000 house
 *     $684K at the ticket window → the track puts up 20% → $137K
 *     Television: 1666 base, +10% new audience, −4% same old faces → 1760
 *     League takes 10% · track keeps 65% · $554K to the purse
 *     ────────────────────────────────────────────────
 *     $691K on the line
 *
 * Every figure is read off `RaceFinanceResult`. Nothing is recomputed here; if
 * a number on this screen is wrong, the engine is wrong, which is the only
 * arrangement under which a balance sheet is worth looking at.
 */

import { useMemo } from 'react';
import type { RaceFinanceResult } from '../domain/finance/raceFinance.ts';
import { formatDollars, formatMegabucks } from '../domain/finance/money.ts';
import { CONTINGENCY_SPONSORS } from '../domain/finance/tables.ts';
import { ordinal } from './boardModel.ts';
import { Cite } from './Cite.tsx';

export interface PaydayProps {
  finance: RaceFinanceResult;
  /** driverId → the name to print. */
  nameOf: (driverId: string) => string;
}

const pct = (n: number): string => `${Math.round(n * 1000) / 10}%`;
const signedPct = (n: number): string => `${n >= 0 ? '+' : ''}${Math.round(n * 1000) / 10}%`;

export default function Payday({ finance, nameOf }: PaydayProps) {
  const { attendance, tv, purse } = finance;

  /** Contingency awards, pulled back out of the ledger rather than re-derived. */
  const contingency = useMemo(
    () =>
      finance.events
        .filter((e) => e.category === 'contingency' && e.amountMb > 0)
        .map((e) => ({ driverId: e.account.id, amountMb: e.amountMb, description: e.description })),
    [finance.events],
  );

  const costs = useMemo(() => {
    const byCategory = new Map<string, number>();
    for (const e of finance.events) {
      if (e.amountMb >= 0) continue;
      if (e.account.kind !== 'team' && e.account.kind !== 'driver') continue;
      byCategory.set(e.category, (byCategory.get(e.category) ?? 0) + -e.amountMb);
    }
    return [...byCategory.entries()].sort((a, b) => b[1] - a[1]);
  }, [finance.events]);

  return (
    <section className="payday" aria-label="The money">
      <header className="payday__head">
        <h2>Payday</h2>
        <p className="payday__total">
          <span className="payday__total-label">On the line</span>
          <span className="payday__total-figure">{formatDollars(purse.prizeMb)}</span>
        </p>
      </header>

      <div className="payday__chain">
        {/* ---- the gate ---------------------------------------------------- */}
        <article className="ledgerbox">
          <h3>At the gate</h3>
          <dl className="chain">
            <div className="chain__row">
              <dt>Grandstand</dt>
              <dd>
                {attendance.capacity.toLocaleString('en-US')}
                {attendance.capacityAssumed && (
                  <span className="chain__flag" title="This track card prints no grandstand size, so a typical one is used">
                    assumed
                  </span>
                )}
              </dd>
            </div>
            <div className="chain__row">
              <dt>
                <span className="die die--2d6">{attendance.roll}</span> {attendance.factor}
              </dt>
              <dd>{pct(attendance.basePct)}</dd>
            </div>
            {attendance.adjustments.map((m) => (
              <div className="chain__row chain__row--mod" key={m.name}>
                <dt>{m.name}</dt>
                <dd>{typeof m.value === 'number' ? signedPct(m.value) : m.value}</dd>
              </div>
            ))}
            <div className="chain__row chain__row--sum">
              <dt>Through the gate</dt>
              <dd>
                {attendance.attendance.toLocaleString('en-US')} <span className="chain__at">at {pct(attendance.finalPct)}</span>
              </dd>
            </div>
            <div className="chain__row">
              <dt>Ticket window</dt>
              <dd>{formatDollars(attendance.admissionsMb)}</dd>
            </div>
            <div className="chain__row chain__row--out">
              <dt>The track puts up 20%</dt>
              <dd>{formatDollars(attendance.trackPurseMb)}</dd>
            </div>
          </dl>
          <Cite as="p" className="ledgerbox__cite">SAG/22 — FAN ATTENDANCE</Cite>
        </article>

        {/* ---- television --------------------------------------------------- */}
        <article className="ledgerbox">
          <h3>Television</h3>
          <dl className="chain">
            <div className="chain__row">
              <dt>Base revenue</dt>
              <dd>{formatMegabucks(tv.baseMb)}</dd>
            </div>
            <div className="chain__row">
              <dt>
                <span className="die die--2d6">{tv.externalRoll}</span> {tv.externalFactor}
              </dt>
              <dd>{signedPct(tv.externalPct)}</dd>
            </div>
            <div className="chain__row chain__row--sum">
              <dt>Modified base</dt>
              <dd>{formatMegabucks(tv.modifiedBaseMb)}</dd>
            </div>
            {tv.internal.length === 0 ? (
              <div className="chain__row chain__row--mod">
                <dt>Nothing on the grid moved the needle</dt>
                <dd>—</dd>
              </div>
            ) : (
              tv.internal.map((m, i) => (
                <div className="chain__row chain__row--mod" key={`${m.name}-${i}`}>
                  <dt>
                    {m.name}
                    {m.note && <span className="chain__note">{m.note}</span>}
                  </dt>
                  <dd>{typeof m.value === 'number' ? signedPct(m.value) : m.value}</dd>
                </div>
              ))
            )}
            <div className="chain__row chain__row--sum">
              <dt>Final base</dt>
              <dd>{formatMegabucks(tv.finalBaseMb)}</dd>
            </div>
            <div className="chain__row chain__row--mod">
              <dt>League takes 10%</dt>
              <dd>−{formatMegabucks(tv.leagueShareMb)}</dd>
            </div>
            <div className="chain__row chain__row--mod">
              <dt>Track keeps 65%</dt>
              <dd>−{formatMegabucks(tv.trackKeepsMb)}</dd>
            </div>
            <div className="chain__row chain__row--out">
              <dt>To the purse</dt>
              <dd>{formatDollars(tv.purseContributionMb)}</dd>
            </div>
          </dl>
          <Cite as="p" className="ledgerbox__cite">SAG/20-21 — TV MONEY · D-011 65/35</Cite>
        </article>

        {/* ---- the purse ---------------------------------------------------- */}
        <article className="ledgerbox ledgerbox--purse">
          <h3>The purse</h3>
          <dl className="chain">
            <div className="chain__row">
              <dt>Track purse</dt>
              <dd>{formatDollars(purse.trackPurseMb)}</dd>
            </div>
            <div className="chain__row">
              <dt>Television</dt>
              <dd>{formatDollars(purse.tvMoneyMb)}</dd>
            </div>
            <div className="chain__row chain__row--sum">
              <dt>Winnings</dt>
              <dd>{formatDollars(purse.winningsMb)}</dd>
            </div>
            <div className="chain__row">
              <dt>Contingency awards</dt>
              <dd>{formatDollars(purse.contingencyMb)}</dd>
            </div>
            <div className="chain__row chain__row--out">
              <dt>The race's prize</dt>
              <dd>{formatDollars(purse.prizeMb)}</dd>
            </div>
            {purse.unclaimedMb > 0.005 && (
              <div className="chain__row chain__row--mod">
                <dt>
                  Unclaimed
                  <span className="chain__note">
                    "the accountant pockets it"<Cite> — SAG/24</Cite>
                  </span>
                </dt>
                <dd>{formatDollars(purse.unclaimedMb)}</dd>
              </div>
            )}
          </dl>
          <p className="ledgerbox__cite">
            <Cite>SAG/23 — </Cite>Driver 40% · team 60%
          </p>
        </article>
      </div>

      {/* ---- who got paid ---------------------------------------------------- */}
      <div className="payday__cols">
        <div className="payday__sheet">
          <h3>The cheques</h3>
          <table className="sheet">
            <thead>
              <tr>
                <th scope="col">Fin</th>
                <th scope="col">Driver</th>
                <th scope="col">Band</th>
                <th scope="col" className="sheet__num">
                  Purse
                </th>
                <th scope="col" className="sheet__num">
                  Driver
                </th>
                <th scope="col" className="sheet__num">
                  Team
                </th>
              </tr>
            </thead>
            <tbody>
              {purse.payouts.map((p) => (
                <tr key={p.driverId} className={p.place === 1 ? 'is-winner' : undefined}>
                  <td className="sheet__place">{ordinal(p.place)}</td>
                  <td className="sheet__name">{nameOf(p.driverId)}</td>
                  <td className="sheet__band">
                    {p.band} <span className="sheet__share">{pct(p.sharePct)}</span>
                  </td>
                  <td className="sheet__num">{formatDollars(p.totalMb)}</td>
                  <td className="sheet__num sheet__num--driver">{formatDollars(p.driverMb)}</td>
                  <td className="sheet__num">{formatDollars(p.teamMb)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ledgerbox__cite">
            Positions 41st and back pay nothing. That is printed, and it is why the bottom of the
            field is a fight.
          </p>
        </div>

        <div className="payday__side">
          <section className="contingency">
            <h3>Contingency row</h3>
            <ul className="contingency__list">
              {contingency.map((c, i) => (
                <li key={`${c.driverId}-${i}`}>
                  <span className="contingency__who">{nameOf(c.driverId)}</span>
                  <span className="contingency__amt">{formatDollars(c.amountMb)}</span>
                  <span className="contingency__why">{c.description}</span>
                </li>
              ))}
              {contingency.length === 0 && <li className="contingency__none">Nothing on the board.</li>}
            </ul>
            <p className="ledgerbox__cite">
              <Cite>SAG/24 — </Cite>The drivers keep every cent of this.{' '}
              <span className="contingency__flavour">
                {CONTINGENCY_SPONSORS['11'].sponsor}: "{CONTINGENCY_SPONSORS['11'].tagline}"
              </span>
            </p>
          </section>

          <section className="costs">
            <h3>Money out</h3>
            <ul className="costs__list">
              {costs.map(([category, amountMb]) => (
                <li key={category}>
                  <span className="costs__cat">{CATEGORY_LABEL[category] ?? category}</span>
                  <span className="costs__amt">{formatDollars(amountMb)}</span>
                </li>
              ))}
            </ul>
            <p className="ledgerbox__cite">Across the whole garage, this weekend.</p>
          </section>

          {finance.sponsorChanges.length > 0 && (
            <section className="sponsors">
              <h3>In the paddock</h3>
              <ul className="sponsors__list">
                {finance.sponsorChanges
                  .filter((c) => c.kind !== 'declined')
                  .slice(0, 8)
                  .map((c, i) => (
                    <li key={`${c.driverId}-${i}`} className={`sponsors__item sponsors__item--${c.kind}`}>
                      {c.description}
                    </li>
                  ))}
              </ul>
              <p className="ledgerbox__cite">
                <Cite>SAG/15-19 — </Cite>Sponsors follow how the week went.
              </p>
            </section>
          )}
        </div>
      </div>
    </section>
  );
}

const CATEGORY_LABEL: Record<string, string> = {
  'race-cost': 'Weekly race cost',
  'research-development': 'Research & development',
  testing: 'Testing',
  repairs: 'Repairs',
  'car-replacement': 'Cars replaced',
  salary: 'Driver salaries',
  fine: 'Fines',
};
